import { useEffect, useState, PropsWithChildren, ReactNode } from "react"
import { useAccountAdd, useAccountLink } from "../../api/mutations"
import { LinkSuccess } from "react-native-plaid-link-sdk"
import { PlaidLink } from "./PlaidLink"

type PlaidAccountAddProps = PropsWithChildren & {
  onSuccess?: () => void
  onExit?: () => void
  onStart?: () => void
  fallback?: ReactNode
}

export function PlaidAccountAdd({
  children,
  onSuccess,
  onExit,
  onStart,
  fallback = null,
}: PlaidAccountAddProps) {
  const [linkToken, setLinkToken] = useState("")
  const [tokenId, setTokenId] = useState("")
  const accountLinkMutation = useAccountLink()
  const {
    mutateAsync: addAccount,
    isPending: isAddingAccount,
    error: isAddingAccountError,
  } = useAccountAdd()

  useEffect(() => {
    if (linkToken || isAddingAccount || isAddingAccountError) {
      return
    }
    async function startPlaidFlow() {
      const response = await addAccount()
      setLinkToken(response.linkToken)
      setTokenId(response.tokenId)
    }

    startPlaidFlow()
  }, [linkToken, isAddingAccount, addAccount, isAddingAccountError])

  async function handleSuccess(success: LinkSuccess) {
    await accountLinkMutation.mutateAsync({
      tokenId,
      publicToken: success.publicToken,
      linkAccountsMetadata: {
        linkSessionId: success.metadata.linkSessionId,
        institutionId: success.metadata.institution?.id,
        accountIds: success.metadata.accounts.map((acc) => acc.id),
      },
      useAccessToken: true,
    })
    if (onSuccess) {
      onSuccess()
    }
  }

  if (!linkToken) {
    return <>{fallback}</>
  }

  return linkToken ? (
    <PlaidLink
      linkToken={linkToken}
      onSuccess={(success) => handleSuccess(success)}
      onExit={() => {
        if (onExit) {
          onExit()
        }
      }}
      onStart={onStart}
      launchOnMount={false}
    >
      {children}
    </PlaidLink>
  ) : null
}
